import { UIIcon } from '../../config/icons';
import { c_cities, cCityBySlug, CCityId } from '../mdl/c-city';
import { c_event_tags, CEventTagId, cEventTagName } from '../mdl/c-event-tag';
import { the_user } from '../me/login';
import { getCountry } from '../me/me';
import { UIDateRangePicker } from '../ui/component/date';
import { UIBaseInput } from '../ui/component/input-base';
import { UISegmented, UISegmentedOptions } from '../ui/component/segmented';
import { UISelect } from '../ui/component/select';
import { UIExclusiveToggles, UIMultiToggles, UIToggleButton, UIToggleButtonOptions } from '../ui/component/toggle';
import { $el } from '../ui/elements';
import { uiMakeI18NValues } from '../ui/functions';
import { UIMenu, uiShowMenu } from '../ui/menu';
import { uiHidePopup, uiShowPopup } from '../ui/popup';
import { UIClassesSpec, UIRelPos, UIValue } from '../ui/types';
import { UIView } from '../ui/view';
import { $on, $trigger } from '../y/events';
import { $i18n } from '../y/i18n';
import { $date, $time, YTime } from '../y/time';

export type ABEventDateFilter = 'today' | 'tomorrow' | 'weekend' | 'week' | 'range';

const tz = 'Europe/Stockholm';

const genre_play_id = '5e8f992cbf50f572a04e7cc9';

export const ab_date_filter_keys: ABEventDateFilter[] = ['today', 'tomorrow', 'week', 'weekend'];

export function abGetDateFilterValues() {
    return uiMakeI18NValues($i18n.tl.o_filter_dates, ab_date_filter_keys);
}

export function abGetRangeForDateFilter(date: ABEventDateFilter): [YTime | undefined, YTime | undefined] {
    const today = $date.today();

    if(date == 'today') {
        return [today.start, today.end];
    } else if(date == 'tomorrow') {
        const tomorrow = today.add(1, $time.day);
        return [tomorrow.start, tomorrow.end];
    } else if(date == 'weekend') {
        const we_start = $date.make(today.year, today.month, today.day_of_month - 1 + (7 - today.day_of_week) % 7);
        return [we_start.start, we_start.add(1, $time.day).end];
    } else if(date == 'week') {
        const we_start = $date.make(today.year, today.month, today.day_of_month - 6 + (7 - today.day_of_week) % 7);
        return [we_start.start, we_start.add(6, $time.day).end];
    }

    return [undefined, undefined];
}

export function abGetEventSortValues(): UIValue<'date' | 'popular'>[] {
    return [{ key: 'popular', name: $i18n.tl.o_filter_sort_recommended }, { key: 'date', name: $i18n.tl.o_filter_sort_date }]
}

export function abGetGenreValues(exclude_play?: boolean): UIValue<CEventTagId>[] {
    return c_event_tags.all.filter(t => (t.is_genre || t.can_select) && (!exclude_play || t.id != genre_play_id)).map(t => ({ key: t.id, name: cEventTagName(t) })).sort((a, b) => a.name.localeCompare(b.name));
}

export function abGetDefaultCity() {
    if(the_user)
        return c_cities.maybe(the_user.city_id)!

    if(getCountry() == 'DE')
        return cCityBySlug('berlin')!;
    else
        return cCityBySlug('stockholm')!;
}

export function abGetCityValues(): UIValue<CCityId>[] {
    const country = getCountry();
    return c_cities.all.filter(c => c.can_signup && c.country == country).map(c => ({ key: c.id, name: c.name }));
}

export function makeCitySegmented(options?: UISegmentedOptions) {
    return new UISegmented(abGetCityValues(), options);
}

export function makeCityToggles() {
    return new UIExclusiveToggles(abGetCityValues(), c => new UIToggleButton({ key: c.key, title: c.name }), { classes: 'ui-toggle-buttons m-vertical' });
}

export function makeCitySelect() {
    return new UISelect(abGetCityValues(), { kind: 'k-frame', classes: 'xx-new', required: true, placeholder: $i18n.tl.h_filter_city });
}

export function makeGenreToggles(exclude_play?: boolean) {
    return new UIMultiToggles(abGetGenreValues(exclude_play), t => new UIToggleButton({ key: t.key, title: t.name }), { classes: 'ui-toggle-buttons' });
}

export function makeGenreExclusiveToggles(exclude_play?: boolean) {
    return new UIExclusiveToggles(abGetGenreValues(exclude_play), t => new UIToggleButton({ key: t.key, title: t.name }), { classes: 'ui-toggle-buttons' });
}

export function makeGenreSelect() {
    const values: UIValue<CEventTagId | undefined>[] = abGetGenreValues()

    values.unshift({ key: undefined, name: 'All' });

    return new UISelect(values, { kind: 'k-frame', classes: 'xx-new m-tint-notempty', required: true, placeholder: $i18n.tl.h_filter_category });
}

export function makeDateFilterExclusiveToggles(on_range: (range: [YTime, YTime]) => void) {
    const values: UIToggleButtonOptions<ABEventDateFilter>[] = [
        {
            key: 'range', icon: UIIcon.CALENDAR, kind: 'k-default', classes: 'm-calendar', on_toggle_click: b => {
                const picker = new UIDateRangePicker;
                $on(picker, 'selected', ([from, to]) => {
                    uiHidePopup(picker);
                    on_range([from.start, to.end]);
                    toggles.value = 'range';
                });
                uiShowPopup(picker, b, { position: UIRelPos.Below | UIRelPos.Start, capture_mouse: true, can_flip: true });

                return false;
            }
        },
        ...ab_date_filter_keys.map(k => ({ key: k, title: $i18n.tl.o_filter_dates[k] }))
    ];
    const toggles = new UIExclusiveToggles(values, c => new UIToggleButton(c), { classes: 'ui-toggle-buttons' });
    return toggles;
}

export function makeEventSortSelect() {
    return new UISelect(abGetEventSortValues(), { kind: 'k-frame', classes: 'xx-new', required: true });
}

export function makeEventSortSegmented() {
    return new UISegmented(abGetEventSortValues(), { classes: 'm-light-border m-flexible' });
}

export function makeDateCorner(time: YTime, classes?: UIClassesSpec) {
    return $el.DIV(['tl-date-corner', classes], [
        $el.SPAN('r-big', time.formatIn('dd', tz)),
        $el.I(time.formatIn('MMM', tz))
    ]);
}

export class TLDateFilterSelect extends UIBaseInput<[ABEventDateFilter?, YTime?, YTime?]> {
    private _date?: ABEventDateFilter;
    private _from?: YTime;
    private _to?: YTime;

    constructor() {
        const input = $el.DIV();
        super({ kind: 'k-frame', classes: 'xx-new m-tint-notempty', r_deco: $el.ICON(UIIcon.ANGLE_DOWN, 'r-arrow') }, $el.DIV());
        this.classList.add('ui-select');
        input.tabIndex = 0;

        this.root.addEventListener('click', () => { this.showDropdown(); });

        this._fixText();
    }

    showDropdown() {
        if(this.disabled) return;

        const menu = new UIMenu([
            ...ab_date_filter_keys.map(k => ({ key: k, title: $i18n.tl.o_filter_dates[k] })),
            { key: 'all', title: $i18n.tl.o_tag_all },
            {
                title: $i18n.tl.o_filter_dates['range'], popup: () => {
                    const picker = new UIDateRangePicker;
                    $on(picker, 'selected', v => {
                        uiHidePopup(picker, true);
                        this._setValue([undefined, v[0].start, v[1].end])
                    });
                    return picker;
                }
            }
        ]);

        uiShowMenu(menu, this);
        menu.on_option = (i) => {
            if(i.key == 'all')
                this._setValue([undefined, undefined, undefined]);
            else if(i.key)
                this._setValue([i.key as ABEventDateFilter, ...abGetRangeForDateFilter(i.key as ABEventDateFilter)]);
        };
    }

    override get is_empty() {
        return !this._date && !this._from && !this._to;
    }

    protected doCheck() {
        this.setOkay(true);
    }

    private _fixText() {
        if(this._date) {
            this.input_elem.textContent = $i18n.tl.o_filter_dates[this._date];
        } else if(this._from && this._to) {
            const from = this._from;
            const to = this._to.add(-1, $time.day);
            if(from.n == to.n)
                this.input_elem.textContent = from.formatIn('dd MMM', tz);
            else if(from.month == to.month && from.year == to.year)
                this.input_elem.textContent = from.formatIn('dd', tz) + ' - ' + to.formatIn('dd MMM', tz);
            else
                this.input_elem.textContent = from.formatIn('dd MMM', tz) + ' - ' + to.formatIn('dd MMM', tz);
        } else {
            this.input_elem.textContent = $i18n.tl.h_filter_date;
        }
    }

    private _setValue(v: [ABEventDateFilter?, YTime?, YTime?]) {
        this.value = v;
        $trigger(this, 'value', v);
    }

    get value(): [ABEventDateFilter?, YTime?, YTime?] {
        return [this._date, this._from, this._to];
    }

    set value(v: [ABEventDateFilter?, YTime?, YTime?] | undefined) {
        v = v || [undefined, undefined, undefined];
        [this._date, this._from, this._to] = v;
        this._fixText();
        this.fixDesign();
    }
}

export class ABStepsView extends UIView {
    private _numbers: HTMLDivElement[];
    private _step: number;

    constructor(amount: number) {
        super('div', 'tl-steps');

        const numbers: HTMLDivElement[] = [];

        for(let i = 0; i < amount; i++) {
            const number = $el.DIV('r-number', (i + 1) + '');
            this.ui_add(number);
            numbers.push(number);
        }

        this._numbers = numbers;
        this._step = -1;
    }

    set step(s: number) {
        if(s == this._step) return;
        this._step = s;

        const numbers = this._numbers;

        for(let i = 0; i < numbers.length; i++) {
            numbers[i].classList.toggle('f-checked', i == s);
        }
    }

    get step() {
        return this._step;
    }
}

export class ABAvailabilityDots extends UIView {
    constructor(available?: number) {
        super('div', 'tl-availability', [$el.DIV('r-dot n-1'), $el.DIV('r-dot n-2'), $el.DIV('r-dot n-3'), $el.DIV('r-dot n-4')]);
        this.available = available;
    }

    set available(available: number | undefined) {
        this.classList.toggle('f-unset', available == undefined);
        this.classList.toggle('f-a4', available != undefined && available > 3);
        this.classList.toggle('f-a3', available != undefined && available > 2);
        this.classList.toggle('f-a2', available != undefined && available > 1);
        this.classList.toggle('f-a1', available != undefined && available > 0);
    }
}
